import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import BrandLogo from "../BrandLogo";

const FallbackScreen = () => {
  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8f9ff 0%, #e8eaff 50%, #f0f2ff 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(146, 145, 239, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(146, 145, 239, 0.08) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Main Content Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          zIndex: 1,
          position: 'relative',
          maxWidth: '700px',
          px: 4,
          animation: 'fadeIn 0.8s ease-out',
          '@keyframes fadeIn': {
            '0%': {
              opacity: 0,
              transform: 'translateY(20px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}
      >
        {/* Logo and Brand Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            mb: 2,
            animation: 'logoFadeIn 1s ease-out 0.2s both',
            '@keyframes logoFadeIn': {
              '0%': {
                opacity: 0,
                transform: 'scale(0.9)',
              },
              '100%': {
                opacity: 1,
                transform: 'scale(1)',
              },
            },
            '& .BrandLogo': {
              transform: 'scale(1.2)',
            },
          }}
        >
          <BrandLogo newline={false} />
        </Box>

        {/* Mission Statement */}
        <Typography
          sx={{
            color: '#2c2c2c',
            textAlign: 'center',
            fontSize: '1.4rem',
            lineHeight: 1.7,
            fontFamily: 'Lato, sans-serif',
            maxWidth: '600px',
            fontWeight: 500,
            animation: 'textFadeIn 1s ease-out 0.4s both',
            '@keyframes textFadeIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(10px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          Empowering exceptional care for neurological disorders in real time through data-driven symptom tracking and analysis.
        </Typography>

        {/* Loading Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            mt: 3,
            animation: 'loadingFadeIn 1s ease-out 0.6s both',
            '@keyframes loadingFadeIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(15px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress
              size={64}
              sx={{
                color: '#9291ef',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                  animation: 'pulse 2s ease-in-out infinite',
                },
                '@keyframes pulse': {
                  '0%, 100%': {
                    opacity: 1,
                  },
                  '50%': {
                    opacity: 0.7,
                  },
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #9291ef 0%, #7c7be5 100%)',
                opacity: 0.15,
                animation: 'glow 2s ease-in-out infinite',
                '@keyframes glow': {
                  '0%, 100%': {
                    opacity: 0.15,
                    transform: 'scale(1)',
                  },
                  '50%': {
                    opacity: 0.25,
                    transform: 'scale(1.1)',
                  },
                },
              }}
            />
          </Box>
          
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: '#2c2c2c',
                fontWeight: 600,
                textAlign: 'center',
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.3rem',
              }}
            >
              Loading...
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                textAlign: 'center',
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.1rem',
                fontWeight: 400,
              }}
            >
              Please wait while we load your content
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Decorative Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '12%',
          right: '12%',
          width: 140,
          height: 140,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(146, 145, 239, 0.06) 0%, rgba(124, 123, 229, 0.06) 100%)',
          filter: 'blur(50px)',
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px)',
            },
            '50%': {
              transform: 'translateY(-20px)',
            },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '18%',
          left: '10%',
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(146, 145, 239, 0.05) 0%, rgba(124, 123, 229, 0.05) 100%)',
          filter: 'blur(40px)',
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />
    </Box>
  );
};

export default FallbackScreen;
